// 1. Framework / library imports
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// 3. API & Types
import { getAdditionalEquipmentByJomAPI } from "services/api/catalog/cvh/api";
import { AdditionalEquipment } from "services/api/catalog/cvh/types";
import {
  getFleetVehicleEquipmentAPI,
  saveFleetVehicleEquipmentAPI,
} from "services/api/fleet/vehicles/equipment/api";
import { FleetVehicleEquipmentItem } from "services/api/fleet/vehicles/equipment/types";

// 4. UI Components
import { TableSearch } from "ui/components/Table/TableSearch";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import Button from "ui/components/Button/Button/Button";
import { Loader } from "ui/components/Loader";
import { ReactSVG } from "react-svg";

// 5. Local
import { CUSTOM_EQUIPMENT_COLUMNS, EQUIPMENT_SUGGESTION_COLUMNS } from "./columns";
import EquipmentAddModal from "./components/EquipmentAddModal";
import { CustomEquipmentDraft, VehicleEquipmentFormProps, VehicleEquipmentFormRef } from "./types";

const VehicleEquipmentForm = forwardRef<VehicleEquipmentFormRef, VehicleEquipmentFormProps>(
  (props, ref) => {
    const { formValues, vehicleInfo, setVehicleInfo } = props;

    const [suggestions, setSuggestions] = useState<AdditionalEquipment[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [customItems, setCustomItems] = useState<FleetVehicleEquipmentItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);

    const initializedKeyRef = useRef<string | null>(null);

    useEffect(() => {
      const id = formValues.id;
      const jom = vehicleInfo.jom;
      if (!id) return;

      const key = `${id}|${jom ?? ""}`;
      if (initializedKeyRef.current === key) return;
      initializedKeyRef.current = key;

      initializeEquipment(id, jom);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.id, vehicleInfo.jom]);

    const initializeEquipment = async (vehicleId: string, jom: string | null) => {
      setIsLoading(true);

      // 1. Dohvati CVH prijedloge po JOM-u (ako JOM postoji)
      let cvhSuggestions: AdditionalEquipment[] = [];
      if (jom) {
        try {
          cvhSuggestions = await getAdditionalEquipmentByJomAPI(jom);
        } catch {
          cvhSuggestions = [];
        }
      }
      setSuggestions(cvhSuggestions);

      // 2. Dohvati već spremljenu opremu vozila
      let saved: FleetVehicleEquipmentItem[] = [];
      try {
        const savedEquipment = await getFleetVehicleEquipmentAPI(vehicleId);
        saved = savedEquipment.map((e) => ({
          cvhId: e.cvhId,
          name: e.name,
          description: e.description,
          priceEur: e.priceEur,
          finalPriceEur: e.finalPriceEur,
          isCustom: e.isCustom,
        }));
      } catch {
        saved = [];
      }

      const savedCvhIds = saved
        .filter((e) => !e.isCustom && e.cvhId != null)
        .map((e) => e.cvhId as number);

      // Ako je oprema već spremljena — poštuj spremljeni odabir.
      // Inače (prvi ulazak) predoznači "uključenu" CVH opremu.
      if (saved.length > 0) {
        setSelected(savedCvhIds);
      } else {
        setSelected(cvhSuggestions.filter((s) => s.included).map((s) => s.id));
      }

      setCustomItems(saved.filter((e) => e.isCustom));

      setIsLoading(false);
    };

    // Sinkroniziraj brojač opreme sa summary karticom
    useEffect(() => {
      setVehicleInfo((prev) => ({
        ...prev,
        equipmentCount: selected.length + customItems.length,
      }));
    }, [selected, customItems, setVehicleInfo]);

    const handleSelectedChange = useSelectedItemsChanges(selected, setSelected);

    const handleOpenAddModal = useCallback(() => {
      setShowAddModal(true);
    }, []);

    const handleCloseAddModal = useCallback(() => {
      setShowAddModal(false);
    }, []);

    const handleAddCustom = useCallback((item: CustomEquipmentDraft) => {
      setCustomItems((prev) => [
        ...prev,
        {
          cvhId: null,
          name: item.name,
          description: item.description.trim() || null,
          priceEur: item.priceEur,
          finalPriceEur: item.priceEur,
          isCustom: true,
        },
      ]);
      setShowAddModal(false);
    }, []);

    // Vlastita oprema nema id — briše se po referenci stavke iz state-a.
    const handleRemoveCustom = useCallback((item: FleetVehicleEquipmentItem) => {
      setCustomItems((prev) => prev.filter((i) => i !== item));
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        saveAll: async () => {
          const id = formValues.id;
          if (!id) return 0;

          const selectedSuggestions: FleetVehicleEquipmentItem[] = suggestions
            .filter((s) => selected.includes(s.id))
            .map((s) => ({
              cvhId: s.id,
              name: s.name ?? "",
              description: s.description,
              priceEur: s.priceEur,
              finalPriceEur: s.finalPriceEur,
              isCustom: false,
            }));

          const items = [...selectedSuggestions, ...customItems];

          try {
            await saveFleetVehicleEquipmentAPI(id, items);
            return items.length;
          } catch {
            return -1;
          }
        },
      }),
      [formValues.id, suggestions, selected, customItems]
    );

    return (
      <div className="flex items-center justify-center px-4 pt-12 pb-6">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Dodatna oprema</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Odaberite opremu koja se nalazi na vozilu prema podacima iz CVH-a ili dodajte vlastitu.
          </p>

          {isLoading ? (
            <div className="relative min-h-[45vh]">
              <Loader isLoading message="Učitavanje dodatne opreme..." />
            </div>
          ) : (
            <div className="">
              {/* CVH prijedlozi */}
              <div className="border border-slate-200 py-6 px-4">
                <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Oprema iz CVH-a</h3>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span>Tvornički uključeno</span>
                  </span>
                </div>

                {suggestions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Nema dostupne CVH opreme za ovo vozilo.
                  </div>
                ) : (
                  <div className="max-h-[45vh] overflow-y-auto border border-slate-200 px-3">
                    <TableSearch
                      data={suggestions}
                      columns={EQUIPMENT_SUGGESTION_COLUMNS}
                      selectableRows={true}
                      selectedRows={selected}
                      onSelectedChange={handleSelectedChange}
                      disablePagination={true}
                      showSelectAll={true}
                    />
                  </div>
                )}
              </div>

              {/* Ručno dodana oprema */}
              <div className="py-6 ">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Vlastita oprema</h3>
                  <Button variant="outlined" size="small" onClick={handleOpenAddModal}>
                    <span className="flex items-center gap-2">
                      <ReactSVG src="/media/icons/plus.svg" className="w-4 h-4" />
                      Dodaj opremu
                    </span>
                  </Button>
                </div>

                {customItems.length > 0 ? (
                  <TableSearch
                    data={customItems}
                    columns={CUSTOM_EQUIPMENT_COLUMNS(handleRemoveCustom)}
                    selectableRows={false}
                    disablePagination={true}
                  />
                ) : (
                  <div className="border-2 border-dashed border-slate-300 rounded-md py-8 text-center text-slate-400 text-sm">
                    Nema ručno dodane opreme.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <EquipmentAddModal
          show={showAddModal}
          onSave={handleAddCustom}
          onClose={handleCloseAddModal}
        />
      </div>
    );
  }
);

export default memo(VehicleEquipmentForm);
